import { useCallback, useEffect, useRef } from 'react';

/**
 * Stable touch data captured from a TouchEvent.
 *
 * React synthetic events are pooled and their properties become invalid
 * after the event handler returns. We capture this stable snapshot at
 * touch start so it remains valid when the long-press callback fires
 * after the async threshold delay.
 */
export interface LongPressTouchData {
  /** Touch X coordinate relative to viewport */
  clientX: number;
  /** Touch Y coordinate relative to viewport */
  clientY: number;
  /** Touch X coordinate relative to page */
  pageX: number;
  /** Touch Y coordinate relative to page */
  pageY: number;
  /** Target element (captured at touch start, remains stable) */
  target: EventTarget | null;
}

interface UseLongPressOptions {
  /** Duration in ms before long-press fires (default: 300) */
  threshold?: number;
  /** Called when long-press threshold is met with stable touch data */
  onLongPress: (data: LongPressTouchData) => void;
}

interface UseLongPressResult {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchMove: () => void;
  onTouchCancel: () => void;
}

/**
 * useLongPress — touch long-press detection hook.
 *
 * Fires `onLongPress` after `threshold` ms of continuous touch contact.
 * Cancelled if the user moves (touchmove) or releases (touchend) before the threshold.
 * Provides optional haptic feedback via navigator.vibrate when available.
 *
 * Note: The callback receives a stable `LongPressTouchData` object instead of
 * the original React event. React synthetic events are pooled and become
 * invalid after the event handler returns, so we capture stable data upfront.
 */
export function useLongPress({
  threshold = 300,
  onLongPress,
}: UseLongPressOptions): UseLongPressResult {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const vibrationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchDataRef = useRef<LongPressTouchData | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (vibrationTimerRef.current !== null) {
      clearTimeout(vibrationTimerRef.current);
      vibrationTimerRef.current = null;
    }
    touchDataRef.current = null;
  }, []);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      // Cancel any existing timer to prevent stale callbacks from prior touches
      cancel();

      // Capture stable touch data IMMEDIATELY while the event is valid.
      // React synthetic events are pooled — properties become null after the
      // handler returns. By capturing now, we have valid data for the callback.
      const touch = e.touches[0];
      if (touch) {
        touchDataRef.current = {
          clientX: touch.clientX,
          clientY: touch.clientY,
          pageX: touch.pageX,
          pageY: touch.pageY,
          target: e.target,
        };
      }

      timerRef.current = setTimeout(() => {
        // Get the captured touch data — if touchend fired during the delay,
        // cancel() would have cleared the ref.
        const capturedData = touchDataRef.current;
        if (!capturedData) {
          timerRef.current = null;
          return;
        }
        // Provide tactile feedback immediately when threshold is crossed
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
          navigator.vibrate(10);
        }
        // Brief delay to ensure vibration registers before UI changes.
        // Track this timer so cancel() can clear it if a new touch starts.
        touchDataRef.current = null;
        vibrationTimerRef.current = setTimeout(() => {
          vibrationTimerRef.current = null;
          onLongPress(capturedData);
        }, 10);
        timerRef.current = null;
      }, threshold);
    },
    [threshold, onLongPress, cancel]
  );

  const onTouchEnd = useCallback(() => {
    cancel();
  }, [cancel]);

  const onTouchMove = useCallback(() => {
    cancel();
  }, [cancel]);

  // Clear pending timer on unmount to prevent firing after unmount
  useEffect(() => {
    return () => cancel();
  }, [cancel]);

  const onTouchCancel = useCallback(() => {
    cancel();
  }, [cancel]);

  return { onTouchStart, onTouchEnd, onTouchMove, onTouchCancel };
}
